import * as React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import { Form, FormActions, FormField } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Input from 'components/inputs/input/Input';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import ChangePasswordModal from '../change-password-modal/ChangePasswordModal';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';
import { saveUserSettings } from '../../reducers/userSettings';
import { fetchCountryCodes } from 'modules/locations/reducers/worldLocation';

import { __t, applyTranslations } from 'utils/language';
import { NAME_MAX_LENGTH, isPhoneNumber, isRequired, maxLength } from 'utils/validators';
import { isPlainInfluencerOrganization } from 'utils/organization';
import { getPhone, sortAndUniqueItems } from 'utils/helper/common';

import './user-account.scss';

applyTranslations(require('./user-account.i18n.json'));

import { Data, MixedSelectDropdownItem } from 'types';

class UserAccount extends React.PureComponent<any, any> {
  static propTypes = {
    isPhoneMandatory: PropTypes.bool,
    dispatch: PropTypes.func,
    account: PropTypes.object
  };

  constructor(props) {
    super(props);

    this.state = {
      user: this.getUserState(props),
      isPasswordModalOpen: false,
      canEdit: false,
      isModified: false,
      countries: []
    };
  }

  componentDidMount() {
    const { user: { phone } } = this.state;
    addUserAction({
      eventCategory: 'drop down',
      eventAction: 'navigate',
      stage: 'finish',
      destination: 'account-settings'
    });

    fetchCountryCodes(phone.country).then(countries => {
      this.setState(prevState => ({ ...prevState, countries }));
    });
  }

  componentDidUpdate(prevProps) {
    const { account } = this.props;

    if (!isEqual(account, prevProps.account)) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        user: this.getUserState(this.props)
      });
    }
  }

  getUserState(nextProps) {
    const { currentAccount: user } = nextProps.account;

    if (!user || !user.id) {
      return {
        firstName: '',
        lastName: '',
        phone: getPhone('')
      };
    }

    return {
      firstName: user.firstName,
      lastName: user.lastName,
      phone: getPhone(user.phone)
    };
  }

  toggleChangePasswordModal = () => {
    this.setState(state => ({
      isPasswordModalOpen: !state.isPasswordModalOpen
    }));
  };

  enterEditMode = () => {
    this.setState({
      canEdit: true,
      isModified: false
    });
  };

  leaveEditMode = (shouldResetState) => {
    this.setState({
      canEdit: false,
      isModified: false,
      user: shouldResetState ?
        this.getUserState(this.props) :
        this.state.user // eslint-disable-line react/no-access-state-in-setstate
    });
  };

  handleSave = () => {
    const { account, dispatch } = this.props;
    const { currentAccount: user } = account;
    const { user: { phone } } = this.state;

    dispatch(saveUserSettings(user.id, { ...this.state.user, phone: `${phone.countryCode} ${phone.number}` }))
      .then(() => this.leaveEditMode(false));
  };

  handleTextInput = (event) => {
    const input = event.target;
    this.setState({
      user: {
        ...this.state.user, // eslint-disable-line react/no-access-state-in-setstate
        [input.name]: input.value
      },
      isModified: true
    });
  };

  handleCountryChange = (items: Array<MixedSelectDropdownItem>, data?: Data | null, selectedCountry?: Partial<MixedSelectDropdownItem> | null) => {
    if (!selectedCountry) {
      return;
    }

    const { value } = selectedCountry;

    this.setState(prevState => {
      const { user: { phone } } = prevState;

      return {
        ...prevState,
        countries: [...items],
        user: {
          ...prevState.user,
          phone: {
            ...phone,
            countryCode: value,
            originalNumber: `${value} ${phone.number}`
          }
        },
        isModified: true
      };
    });
  };

  handlePhoneNumberEdit = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event;

    this.setState(prevState => {
      const { user: { phone } } = prevState;

      return {
        ...prevState,
        user: {
          ...prevState.user,
          phone: {
            ...phone,
            number: value,
            originalNumber: `${phone.countryCode} ${value}`

          }
        },
        isModified: true
      };
    });
  };

  render() {
    const { account, isPhoneMandatory } = this.props;
    const { currentAccount: user } = account;
    const { isModified, canEdit, countries } = this.state;
    const state = this.state;

    if (!user || !user.id) {
      return null;
    }

    const editModeButtons = [
      <ActionButton
        key="cancel"
        color="tertiary"
        onClick={() => this.leaveEditMode(true)}
        data={{ qa: 'user-account__button-cancel' }}
      >
        {__t('user_account.button_cancel')}
      </ActionButton>,
      <ActionButton
        key="save"
        type="submit"
        data={{ qa: 'user-account__button-submit' }}
      >
        {__t('user_account.button_save')}
      </ActionButton>
    ];

    const viewModeButtons = [
      <ActionButton
        key="edit"
        onClick={this.enterEditMode}
        data={{ qa: 'user-account__button-edit' }}
      >
        {__t('user_account.button_edit')}
      </ActionButton>,
      <ActionButton
        key="change"
        onClick={this.toggleChangePasswordModal}
        data={{ qa: 'user-account__button-change-password' }}
      >
        {__t('user_account.title')}
      </ActionButton>
    ];

    return (
      <section>
        {
          isModified &&
            <PromptExit type="page" />
        }
        <Form
          key={canEdit}
          onSubmit={this.handleSave}
        >
          <FormField
            label={__t('user_account.signup_first_name')}
            fontSize="small"
            showRequiredMark
            validators={[
              isRequired(state.user.firstName),
              maxLength(state.user.firstName, NAME_MAX_LENGTH)
            ]}
          >
            <Input
              name="firstName"
              value={state.user.firstName}
              disabled={!state.canEdit}
              onChange={this.handleTextInput}
              data={{ qa: 'user-account__input-firstname' }}
            />
          </FormField>

          <FormField
            label={__t('user_account.signup_last_name')}
            fontSize="small"
            showRequiredMark
            validators={[
              isRequired(state.user.lastName),
              maxLength(state.user.lastName, NAME_MAX_LENGTH)
            ]}
          >
            <Input
              name="lastName"
              value={state.user.lastName}
              disabled={!state.canEdit}
              onChange={this.handleTextInput}
              data={{ qa: 'user-account__input-lastname' }}
            />
          </FormField>

          <FormField
            label={__t('user_account.signup_email')}
            fontSize="small"
            showRequiredMark
          >
            <Input
              value={user.email}
              disabled
              data={{ qa: 'user-account__email' }}
            />
          </FormField>
          <FormField
            label={__t('user_account.signup_mobile_number')}
            fontSize="small"
            showRequiredMark={isPhoneMandatory}
            validators={
              isPhoneMandatory ?
                [
                  isRequired(state.user.phone.number),
                  maxLength(state.user.phone.number, NAME_MAX_LENGTH),
                  isPhoneNumber(`${state.user.phone.countryCode}${state.user.phone.number}`, 'This is not a valid phone number.')
                ] :
                [
                  maxLength(state.user.phone.number, NAME_MAX_LENGTH)
                ]
            }
          >
            <div className={state.canEdit ? 'user-account__phone-number-edit' : ''}>
              {
                state.canEdit && (
                  <MixedSelectDropdown
                    name="countryCode"
                    items={sortAndUniqueItems(countries)}
                    onItemsToggle={this.handleCountryChange}
                    withSearchable
                    className="user-account__country-select"
                  />
                )
              }
              <Input
                name="phone"
                value={state.canEdit ? state.user.phone.number : state.user.phone.originalNumber}
                disabled={!state.canEdit}
                onChange={this.handlePhoneNumberEdit}
                className="user-account__phone-input"
              />
            </div>
          </FormField>
          <FormActions>
            {
              state.canEdit ? editModeButtons : viewModeButtons
            }
          </FormActions>
        </Form>
        <ChangePasswordModal
          open={this.state.isPasswordModalOpen}
          onClose={this.toggleChangePasswordModal}
        />
      </section>
    );
  }
}

function mapStateToProps({ account, organizations }) {
  const isPhoneMandatory = isPlainInfluencerOrganization(organizations.currentOrganization);
  return {
    isPhoneMandatory,
    account
  };
}

export default connect(mapStateToProps)(UserAccount);
